import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

export interface GroupItem {
  id: number;
  name: string;
}

export interface GroupSelectionDialogData {
  groups: GroupItem[];
  selectedGroupId: string | number;
}

@Component({
  selector: 'app-group-selection-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
  ],
  template: `
    <h2 mat-dialog-title>Vælg brugergruppe</h2>
    <div mat-dialog-content>
      <mat-form-field appearance="outline" class="search-field">
        <mat-icon matPrefix>search</mat-icon>
        <input
          matInput
          [(ngModel)]="searchText"
          (input)="filterGroups()"
          placeholder="Søg efter grupper..."
        />
        @if (searchText) {
        <button
          matSuffix
          mat-icon-button
          aria-label="Clear"
          (click)="searchText = ''; filterGroups()"
        >
          <mat-icon>close</mat-icon>
        </button>
        }
      </mat-form-field>

      <div class="groups-grid">
        <div
          class="group-card"
          [class.active]="selectedGroupId === 'all' || !selectedGroupId"
          (click)="selectGroup('all')"
        >
          <mat-icon>public</mat-icon>
          <span>Alle grupper</span>
        </div>

        @for (group of paginatedGroups; track group) {
        <div
          class="group-card"
          [class.active]="selectedGroupId == group.id"
          (click)="selectGroup(group.id)"
        >
          <mat-icon>group</mat-icon>
          <span>{{ group.name }}</span>
        </div>
        }
      </div>

      <mat-paginator
        [length]="filteredGroups.length"
        [pageSize]="pageSize"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="onPageChange($event)"
      >
      </mat-paginator>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button (click)="close()">Annuller</button>
      <button mat-raised-button color="primary" (click)="apply()">
        Anvend
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .search-field {
        width: 100%;
        margin-bottom: 16px;
      }

      .groups-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 12px;
        margin-bottom: 16px;
        max-height: min(520px, 58vh);
        overflow-y: auto;
      }

      .group-card {
        background-color: #f5f5f5;
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;

        mat-icon {
          font-size: 24px;
          color: #757575;
        }

        span {
          font-size: 13px;
          font-weight: 500;
          word-break: break-word;
        }

        &:hover {
          background-color: #e0e0e0;
        }

        &.active {
          background-color: #e8f5e9;

          mat-icon {
            color: #2e7d32;
          }
        }
      }
    `,
  ],
})
export class GroupSelectionDialogComponent implements OnInit {
  searchText = '';
  filteredGroups: GroupItem[] = [];
  paginatedGroups: GroupItem[] = [];
  selectedGroupId: string | number;

  pageSize = 25;
  currentPage = 0;

  constructor(
    public dialogRef: MatDialogRef<GroupSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GroupSelectionDialogData
  ) {
    this.selectedGroupId = data.selectedGroupId ?? 'all';
  }

  ngOnInit(): void {
    this.filteredGroups = [...this.data.groups];
    this.updatePaginatedGroups();
  }

  filterGroups(): void {
    if (!this.searchText) {
      this.filteredGroups = [...this.data.groups];
    } else {
      const searchTerm = this.searchText.toLowerCase();
      this.filteredGroups = this.data.groups.filter((g) =>
        g.name.toLowerCase().includes(searchTerm)
      );
    }
    this.currentPage = 0;
    this.updatePaginatedGroups();
  }

  updatePaginatedGroups(): void {
    const startIndex = this.currentPage * this.pageSize;
    this.paginatedGroups = this.filteredGroups.slice(
      startIndex,
      startIndex + this.pageSize
    );
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedGroups();
  }

  selectGroup(id: string | number): void {
    this.selectedGroupId = id;
  }

  apply(): void {
    this.dialogRef.close(this.selectedGroupId);
  }

  close(): void {
    this.dialogRef.close();
  }
}
